import type { ReactNode } from 'react';

export interface NotificationProps {
  /** Left badge/icon (e.g. the buyback coins). Optional. */
  icon?: ReactNode;
  /** Body — rich text allowed (highlight spans, etc.). */
  children: ReactNode;
  /** Extra classes on the pill. */
  className?: string;
  /** `data-co-component` for the Design Constructor inspector. Defaults
   *  to `Notification`; per-use consumers can override. */
  inspectName?: string;
  /** `pill` (default) — the rounded glassy bar; `card` — sleek's flat
   *  16px-radius card with 14px copy (partner Figma 2026-10-08). */
  variant?: 'pill' | 'card';
}

/**
 * Reusable notification "pill" — Figma 1251:1131. A rounded, glassy bar
 * with an optional left icon badge and a body line. Used app-wide (e.g.
 * the buyback confirmation) so the look stays consistent; render it
 * directly or hand it to a toast via `toast.custom`.
 *
 * Themeable through the Design Constructor. Each slot resolves in cascade:
 *   var(--co-<inspectName>-<slot>, var(--co-Notification-<slot>, <default>))
 * so a named consumer (e.g. `BuybackNotification`) can override on top
 * of the generic Notification knobs while still inheriting any unset
 * slot from the shared schema. Slots:
 *   bgFrom  pill background — top gradient stop
 *   bgTo    pill background — bottom gradient stop
 *   border  hairline border colour
 *   shadow  drop shadow
 *   text    body text colour
 *   accent  highlight colour (e.g. the green amount)
 */
// Per-instance CSS-var blocks must spell each var name out in full so the
// theme-constructor's schema-invariant test (a regex grep over source
// text) can prove each registry knob is read. Only the fallback values are
// interpolated; building the var NAMES from a template literal would hide
// them from the scanner and trip the invariant.
type Fallbacks = { background: string; border: string; shadow: string };

const PILL_FALLBACKS: Fallbacks = {
  background: '#15181E',
  border: 'rgba(255,255,255,0.08)',
  shadow: '0px 24px 24px 0px rgba(0,0,0,0.25)',
};

/** Sleek's flat card (partner Figma 2026-10-08): no shadow, a near-invisible
 *  border on the same surface as the sheets. */
const CARD_FALLBACKS: Fallbacks = {
  background: '#1A1B1F',
  border: 'rgba(255,255,255,0.01)',
  shadow: 'none',
};

function buybackTokens({ background, border, shadow }: Fallbacks) {
  return {
    background: `linear-gradient(180deg, var(--co-BuybackNotification-bgFrom, var(--co-Notification-bgFrom, ${background})) 0%, var(--co-BuybackNotification-bgTo, var(--co-Notification-bgTo, ${background})) 100%)`,
    borderColor: `var(--co-BuybackNotification-border, var(--co-Notification-border, ${border}))`,
    boxShadow: `var(--co-BuybackNotification-shadow, var(--co-Notification-shadow, ${shadow}))`,
    text: 'var(--co-BuybackNotification-text, var(--co-Notification-text, #FFFFFF))',
  };
}

function defaultTokens({ background, border, shadow }: Fallbacks) {
  return {
    background: `linear-gradient(180deg, var(--co-Notification-bgFrom, ${background}) 0%, var(--co-Notification-bgTo, ${background}) 100%)`,
    borderColor: `var(--co-Notification-border, ${border})`,
    boxShadow: `var(--co-Notification-shadow, ${shadow})`,
    text: 'var(--co-Notification-text, #FFFFFF)',
  };
}

// Mobile (≤600px — sonner's own full-width breakpoint, so the box matches
// the toast slot) spans the full width as a bar; desktop hugs its content.
const SHELL_CLASS = {
  pill: 'inline-flex w-auto items-center gap-3 rounded-full border border-solid pl-1.5 pr-[26px] py-1.5 max-[600px]:flex max-[600px]:w-full',
  card: 'inline-flex w-auto max-w-[362px] items-center gap-4 rounded-2xl border border-solid p-4 max-[600px]:flex max-[600px]:w-full max-[600px]:max-w-none',
} as const;

const TEXT_CLASS = {
  pill: 'text-[15px] leading-[20px] tracking-[-0.3px] max-w-[232px] max-[600px]:max-w-none max-[600px]:flex-1',
  card: 'flex-1 text-[14px] leading-[17px]',
} as const;

export function Notification({
  icon,
  children,
  className = '',
  inspectName = 'Notification',
  variant = 'pill',
}: NotificationProps) {
  const fallbacks = variant === 'card' ? CARD_FALLBACKS : PILL_FALLBACKS;
  const tokens = inspectName === 'BuybackNotification' ? buybackTokens(fallbacks) : defaultTokens(fallbacks);

  return (
    <div
      data-co-component={inspectName}
      role="status"
      className={`${SHELL_CLASS[variant]} ${className}`.trim()}
      style={{
        background: tokens.background,
        borderColor: tokens.borderColor,
        boxShadow: tokens.boxShadow,
      }}
    >
      {icon && <span className="shrink-0">{icon}</span>}
      <span className={`min-w-0 font-semibold ${TEXT_CLASS[variant]}`} style={{ color: tokens.text }}>
        {children}
      </span>
    </div>
  );
}
